import { Link } from "react-router-dom";
import { Download, Mail, Phone, Copy, Check, ExternalLink, Facebook, Linkedin } from "lucide-react";
import { useEffect, useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import api from "@/lib/api";

/**
 * Press / Media Kit page — a canonical resource for journalists, podcasters,
 * conference organizers, and partners. Serves 3 purposes simultaneously:
 *   1. Backlink magnet: press kits get linked when the org is cited.
 *   2. Trust signal: search engines index the boilerplate + stats.
 *   3. Sales support: prospects & partners can share a 1-stop reference.
 */

const STATS = [
  { value: "10+", label: "years leading BH operations" },
  { value: "50+", label: "programs stood up or restructured" },
  { value: "$100M+", label: "in payroll & program budgets managed" },
  { value: "12", label: "states served across the U.S." },
];

const BOILERPLATES = [
  {
    title: "Short (25 words)",
    text:
      "Catalyst BH Consulting helps behavioral-healthcare organizations start, staff, and scale — combining hands-on HR leadership with clinical-program development from a team of behavioral-health veterans.",
  },
  {
    title: "Medium (60 words)",
    text:
      "Catalyst BH Consulting is a Phoenix-based advisory firm serving behavioral-healthcare organizations across the U.S. The team pairs decades of frontline experience with a modern program-development platform — Catalyst Continuum — to help operators open new programs, pass accreditation, hire and retain clinical staff, and grow revenue. Services include HR consulting, program startup, survey readiness, curriculum, and executive coaching.",
  },
  {
    title: "Long (120 words)",
    text:
      "Catalyst BH Consulting (Catalyst Behavioral Health Consulting, LLC) is a Phoenix-based advisory firm dedicated to helping behavioral-health organizations build, grow, and thrive. Its two divisions — Catalyst HR and Catalyst Continuum — combine hands-on human-resources leadership with clinical-program development. The founding team brings a decade-plus of experience leading IOP, PHP, and residential programs across the country, and today serves operators in 12+ states. Catalyst's programs include fractional HR support, compensation studies, program startup packages, survey-readiness assessments, and the Catalyst Academy digital library. The company was founded on the belief that behavioral-healthcare should be run with the same operational rigor as any other essential service — without losing the humanity at the center of the work.",
  },
];

const QUOTES = [
  {
    quote:
      "You cannot fix a broken program with more clinicians. You fix it with better structure, better training, and better hiring. That is our whole job.",
    attribution: "Kristin Hale, Co-Founder",
  },
  {
    quote:
      "Ninety percent of new behavioral-health programs close within five years. Most of those failures are HR and operations failures — not clinical failures.",
    attribution: "Catalyst BH Consulting research, 2026",
  },
];

const LOGO_ASSETS = [
  { name: "Wordmark · Navy on cream (SVG)", href: "/press/catalyst-wordmark-navy.svg", type: "SVG" },
  { name: "Wordmark · Gold on navy (SVG)", href: "/press/catalyst-wordmark-gold-on-navy.svg", type: "SVG" },
  { name: "Icon only · Navy tile", href: "/apple-touch-icon.png", type: "PNG" },
  { name: "OG social card (1200×630)", href: "/og-catalyst.svg", type: "SVG" },
];

function BoilerplateCard({ title, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="border border-[var(--line)] bg-white p-6" data-testid={`boilerplate-${title.split(' ')[0].toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="eyebrow-dark">{title}</span>
        <button
          onClick={copy}
          className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 border transition-colors ${
            copied ? "border-emerald-600 text-emerald-700" : "border-[var(--line)] text-[var(--navy)] hover:bg-[var(--cream)]"
          }`}
          data-testid={`boilerplate-copy-${title.split(' ')[0].toLowerCase()}`}
        >
          {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
        </button>
      </div>
      <p className="text-sm text-[var(--ink)] leading-relaxed">{text}</p>
    </div>
  );
}

export default function Press() {
  const [founders, setFounders] = useState([]);
  useEffect(() => {
    api.get("/founders").then((r) => setFounders(r.data || [])).catch(() => {});
  }, []);

  return (
    <SiteLayout branch="main">
      <SEO
        title="Press & Media Kit"
        description="Media resources for Catalyst BH Consulting — logos, boilerplate, headshots, statistics, and quotable material."
        path="/press"
      />

      {/* HERO */}
      <section className="section bg-[var(--navy)] text-white">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-[3fr,2fr] gap-12 items-end">
          <div>
            <span className="eyebrow-light">FOR JOURNALISTS · PODCASTERS · CONFERENCE ORGANIZERS</span>
            <h1 className="font-display mt-6 text-[clamp(40px,5vw,64px)] leading-[1.02]">
              Press & <span className="text-[var(--gold)]">Media Kit</span>
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-xl">
              Everything you need to cite Catalyst BH Consulting — logos, boilerplate,
              headshots, statistics, and quotable positions. All materials are cleared for
              editorial use.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3" data-testid="press-stats">
            {STATS.map((s) => (
              <div key={s.label} className="border border-white/20 p-4">
                <div className="font-display text-3xl text-[var(--gold)]">{s.value}</div>
                <div className="text-[11px] text-white/70 mt-1 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOILERPLATE */}
      <section className="section bg-[var(--cream)]">
        <div className="max-w-[1200px] mx-auto">
          <Reveal className="max-w-2xl">
            <span className="eyebrow-dark">BOILERPLATE</span>
            <h2 className="font-display mt-5 text-[clamp(30px,3vw,44px)] leading-[1.1] text-[var(--navy)]">
              Approved company descriptions — three lengths.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {BOILERPLATES.map((b) => <BoilerplateCard key={b.title} {...b} />)}
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <section className="section bg-white" aria-labelledby="logos-h">
        <div className="max-w-[1200px] mx-auto">
          <Reveal className="max-w-2xl">
            <span className="eyebrow-dark">LOGOS & VISUAL ASSETS</span>
            <h2 id="logos-h" className="font-display mt-5 text-[clamp(30px,3vw,44px)] leading-[1.1] text-[var(--navy)]">
              Download-ready in SVG and PNG.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="press-logos">
            {LOGO_ASSETS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                download
                className="group border border-[var(--line)] bg-[var(--cream)] p-6 hover:bg-white transition-colors block"
                data-testid={`logo-${l.type.toLowerCase()}`}
              >
                <div className="text-[10px] tracking-widest font-bold text-[var(--gold)]">{l.type}</div>
                <div className="font-display text-base text-[var(--navy)] mt-2 group-hover:underline decoration-[var(--gold)] decoration-2 underline-offset-4">{l.name}</div>
                <div className="text-[11px] text-[var(--muted)] mt-3 inline-flex items-center gap-1"><Download size={11} /> Download</div>
              </a>
            ))}
          </div>
          <p className="text-xs text-[var(--muted)] mt-6 italic">
            When possible, please link back to <Link to="/" className="text-[var(--navy)] underline decoration-[var(--gold)]">catalystbhconsulting.com</Link> when using these assets.
          </p>
        </div>
      </section>

      {/* FOUNDERS / HEADSHOTS */}
      {founders.length > 0 && (
        <section className="section bg-[var(--cream)]" aria-labelledby="founders-h">
          <div className="max-w-[1200px] mx-auto">
            <Reveal className="max-w-2xl">
              <span className="eyebrow-dark">FOUNDER HEADSHOTS & BIOS</span>
              <h2 id="founders-h" className="font-display mt-5 text-[clamp(30px,3vw,44px)] leading-[1.1] text-[var(--navy)]">
                Approved headshots — right-click to save.
              </h2>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-8 mt-10" data-testid="press-founders">
              {founders.map((f) => (
                <div key={f.id} className="border border-[var(--line)] bg-white overflow-hidden flex" data-testid={`press-founder-${f.id}`}>
                  {f.photo_url && (
                    <img
                      src={f.photo_url}
                      alt={`${f.name} — headshot`}
                      className="w-32 h-full object-cover flex-shrink-0"
                      loading="lazy"
                    />
                  )}
                  <div className="p-5">
                    <h3 className="font-display text-xl text-[var(--navy)]">{f.name}</h3>
                    <div className="text-[11px] tracking-widest uppercase text-[var(--gold)] font-bold mt-1">{f.title}</div>
                    <p className="text-sm text-[var(--ink)] mt-3 leading-relaxed">{f.bio}</p>
                    {f.photo_url && (
                      <a href={f.photo_url} download className="mt-3 inline-flex items-center gap-1 text-[11px] text-[var(--navy)] font-bold hover:text-[var(--gold)]">
                        <Download size={11} /> Download headshot
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTES */}
      <section className="section bg-white" aria-labelledby="quotes-h">
        <div className="max-w-[1000px] mx-auto">
          <Reveal>
            <span className="eyebrow-dark">QUOTABLE POSITIONS</span>
            <h2 id="quotes-h" className="font-display mt-5 text-[clamp(30px,3vw,44px)] leading-[1.1] text-[var(--navy)]">
              Ready-to-cite statements.
            </h2>
          </Reveal>
          <ul className="mt-10 space-y-8" data-testid="press-quotes">
            {QUOTES.map((q) => (
              <li key={q.quote} className="border-l-4 border-[var(--gold)] pl-6">
                <blockquote className="font-display text-xl text-[var(--navy)] leading-snug italic">
                  &ldquo;{q.quote}&rdquo;
                </blockquote>
                <cite className="block mt-3 text-[11px] tracking-widest uppercase text-[var(--muted)] not-italic font-bold">
                  — {q.attribution}
                </cite>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section bg-[var(--navy)] text-white">
        <div className="max-w-[900px] mx-auto text-center">
          <span className="eyebrow-light">PRESS INQUIRIES</span>
          <h2 className="font-display mt-5 text-[clamp(30px,3vw,44px)]">
            Reach us within one business day.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href="mailto:press@catalystbhconsulting.com?subject=Press%20Inquiry"
              className="btn btn-gold inline-flex items-center gap-2"
              data-testid="press-email"
            >
              <Mail size={14} /> press@catalystbhconsulting.com
            </a>
            <a
              href="tel:+16233243237"
              className="btn btn-ghost !text-white !border-white/40 inline-flex items-center gap-2"
              data-testid="press-phone"
            >
              <Phone size={14} /> +1 (623) 324-3237
            </a>
          </div>
          <p className="text-white/60 text-xs mt-8">
            Prefer to book directly? <Link to="/book" className="underline decoration-[var(--gold)]">Reserve a call →</Link>
          </p>
          <div className="flex justify-center gap-3 mt-6">
            <a
              href="https://facebook.com/catalystbhconsulting.com"
              target="_blank" rel="noopener"
              aria-label="Facebook"
              className="w-10 h-10 border border-white/30 hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center justify-center transition-colors"
              data-testid="press-facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href="https://www.linkedin.com/company/catalyst-behavioral-health-consulting"
              target="_blank" rel="noopener"
              aria-label="LinkedIn"
              className="w-10 h-10 border border-white/30 hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center justify-center transition-colors"
              data-testid="press-linkedin"
            >
              <Linkedin size={16} />
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
